import React, { useState, useEffect } from 'react'
import PropTypes from 'prop-types'
import moment from 'moment'
import styled from 'styled-components'

import { ColumnFlex, Flex } from '~/components/common/containers'
import Spinner from '~/components/common/spinner'
import { translate } from '~/core/translate'
import Delimiter from '~/components/common/delimiter'
import { getPriceString } from '../core/utils'
import { getTeamMembersCount, getDomainDetectionStatus } from '../services/api'
import { showErrorNotification } from '~/core/ui'
import Info from '~/components/common/info-box'

const DATE_FORMAT = 'Do MMM YYYY'

const Label = styled.span`
  text-align: left;
`

const Value = styled.b`
  margin-left: auto;
  text-align: right;
`

const Row = styled(Flex)`
  padding: 14px 0;
  font-size: 13px;
`

const PricePerUserLabel = styled(Label)`
  sup {
    font-size: 8px;
  }
`

const BillingInterval = styled.b`
  margin: 0 auto 8px 0;
`

const Container = styled(ColumnFlex)`
  border: 1px solid #F2F2F2;
  border-radius: 4px;
  width: 100%;
  padding: 8px 16px;
  box-sizing: border-box;
  margin-top: 24px;
  margin-bottom: 16px;
`

const noteMap = {
  done: {
    domainDetectionOn: 'teammates_added_later_info_domain_on',
    domainDetectionOff: 'teammates_added_later_info'
  },
  preview: {
    domainDetectionOn: 'all_team_upgraded_info_domain_on',
    domainDetectionOff: 'all_team_upgraded_info'
  }
}

const OrderSummary = ({ plan, complete = false }) => {
  if (!plan) {
    return <Spinner />
  }

  const [usersCount, setUsersCount] = useState(0)
  const [note, setNote] = useState(null)

  const { amount, currency, price } = plan
  const [value, decimals] = price.split('.')
  const totalPrice = getPriceString(currency, Math.floor(amount * usersCount * 100) / 100)
  const usersLabel = usersCount === 1 ? translate('user') : translate('users')
  const getPricePerUserLabel = () => (
    <PricePerUserLabel>
      <span>{usersCount} </span>
      <span>{usersLabel} </span>
      <span>x {value}</span>
      {Boolean(decimals) && <>.{decimals}</>}
      <span> x {translate('one_month')}</span>
    </PricePerUserLabel>
  )

  const init = async () => {
    try {
      const [countResponse, domainDetection] = await Promise.all([getTeamMembersCount(), getDomainDetectionStatus()])
      setUsersCount(countResponse)
      setNote(noteMap[complete ? 'done' : 'preview'][domainDetection ? 'domainDetectionOn' : 'domainDetectionOff'])
    } catch (err) {
      showErrorNotification('Failed to retrieve number of users')
    }
  }

  useEffect(() => {
    init()
  }, [])

  if (!note) {
    return <Spinner />
  }

  // eslint-disable-next-line no-unreachable
  return (
    <>
      {complete && <BillingInterval>{translate(plan.billingRecurrence)}</BillingInterval>}
      <Container fullWidth>
        <Row>
          {getPricePerUserLabel()}
          <Value>{totalPrice}</Value>
        </Row>

        <Delimiter isFullLength />

        <Row>
          <Label>{translate('free_trial_period')}</Label>
          <Value>{moment().add(1, 'month').format(DATE_FORMAT)}</Value>
        </Row>
      </Container>

      <Info>
        {translate(note)}
      </Info>
    </>
  )
}

OrderSummary.propTypes = {
  complete: PropTypes.bool,
  plan: PropTypes.shape({
    id: PropTypes.string,
    amount: PropTypes.number,
    currency: PropTypes.string,
    price: PropTypes.string,
    recurrence: PropTypes.string,
    billingRecurrence: PropTypes.string
  })
}

export default OrderSummary
